import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import { LoadingShell, ErrorState } from '@siliconx/shared/ui/state';
import { qk } from '../../../lib/queryKeys';
import { useAuth } from '../../../lib/authContext';
import {
  createBenchOrgPlan,
  getBenchMandatorySet,
  getBenchPlan,
  isPlansDisabled,
  planErrorCode,
  planErrorStatus,
} from '../../../lib/benchPlans';
import { PlanBuilder } from '../../../components/bench/PlanBuilder';
import { blankDraft, draftFromPlan, draftToPayload, planErrorMessage, type PlanDraft } from '../../../components/bench/planModel';

/**
 * SIL-2246 U1. New org plan, blank or copied from a SiliconX template (?from=<planId>). A blank plan starts with the
 * mandatory rows locked. Org-admin only; the create definer is the boundary.
 */
export const Route = createFileRoute('/bench/plans/new')({
  validateSearch: (search: Record<string, unknown>): { from?: string } =>
    typeof search.from === 'string' ? { from: search.from } : {},
  component: NewPlanRoute,
});

function NewPlanRoute() {
  const { from } = Route.useSearch();
  return <NewBenchPlanView from={from ?? null} />;
}

export function NewBenchPlanView({ from }: { from: string | null }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [draft, setDraft] = useState<PlanDraft>(() => blankDraft());
  const [loadedFrom, setLoadedFrom] = useState<string | null>(null);

  const mandatory = useQuery({ queryKey: qk.bench.mandatorySet(), queryFn: getBenchMandatorySet, retry: false, staleTime: 60_000 });
  const template = useQuery({
    queryKey: qk.bench.plan(from ?? ''),
    queryFn: () => getBenchPlan(from!),
    enabled: from !== null,
    retry: false,
  });

  // Seed the draft once from the template; the admin's edits are never overwritten afterwards.
  useEffect(() => {
    if (template.data && loadedFrom !== template.data.id) {
      setLoadedFrom(template.data.id);
      setDraft(draftFromPlan(template.data));
      setName((n) => n || `${template.data.name} copy`.slice(0, 120));
    }
  }, [template.data, loadedFrom]);

  const create = useMutation({
    mutationFn: () => {
      const payload = draftToPayload(draft);
      return createBenchOrgPlan({
        name: name.trim(),
        ...payload,
        ...(template.data ? { originPlanId: template.data.id, originVersion: template.data.version } : {}),
      });
    },
    onSuccess: ({ planId }) => {
      void queryClient.invalidateQueries({ queryKey: qk.bench.plansAll() });
      void navigate({ to: '/bench/plans/$planId', params: { planId } });
    },
  });

  if ((template.isError && isPlansDisabled(template.error)) || (mandatory.isError && isPlansDisabled(mandatory.error))) {
    return (
      <div className="space-y-6">
        <PageHeader title="New bench plan" />
        <InsightBanner tone="neutral" lead="Plans are not available yet." />
      </div>
    );
  }

  const trimmed = name.trim();
  const createErr = create.isError
    ? (planErrorMessage(planErrorCode(create.error), planErrorStatus(create.error)) ?? 'The plan could not be saved. Try again.')
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="New bench plan"
        subtitle={template.data ? `Copied from SiliconX template ${template.data.name} v${template.data.version}.` : 'Starts with the SiliconX mandatory tests locked.'}
        actions={<Link to="/bench/plans" className="text-sm text-muted-foreground hover:underline">Back to plans</Link>}
      />
      {!isAdmin && (
        <InsightBanner tone="neutral" lead="Only an org admin can create plans." />
      )}
      {from !== null && template.isPending && <LoadingShell rows={4} />}
      {template.isError && !isPlansDisabled(template.error) && <ErrorState error={template.error} onRetry={() => void template.refetch()} />}
      {(from === null || template.data) && (
        <>
          <Card className="space-y-2">
            <label htmlFor="bench-plan-name" className="block text-sm font-medium">Plan name</label>
            <input
              id="bench-plan-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              disabled={!isAdmin}
              autoComplete="off"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <p className="text-xs text-muted-foreground">Names are unique within your organization.</p>
          </Card>
          <PlanBuilder draft={draft} mandatory={mandatory.data ?? null} readOnly={!isAdmin || create.isPending} onChange={setDraft} />
          {createErr && <p role="alert" className="text-sm text-status-red">{createErr}</p>}
          <div className="flex gap-3">
            <Button onClick={() => create.mutate()} disabled={!isAdmin || trimmed.length === 0 || create.isPending}>
              Create plan
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
